import React, { useEffect } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WelcomeWrapper from "./Welcome.style";
import { RegistrationLayout } from "../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../hook/useRegistrationStepFocus";
import { RegistrationContinueArrow } from "../RegistrationLayout/RegistrationFieldError";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
  appleUrl,
  googleUrl,
} from "../../helper/constants";
import NutritionLogo from "../../assets/images/nutritionlogo.png";
import AppStoreImage from "../../assets/images/AppStoreBadge.png";
import GooglePlayImage from "../../assets/images/GooglePlayBadge.png";
import {
  clearRegistrationData,
  isRegistrationComplete,
} from "../RegistrationSetup/registrationSetupStorage";
import { trackRegistrationStarted } from "../../helper/registrationAnalytics";
import { resolveTranslatedText } from "../../helper/utils";

const WELCOME_STEP = REGISTRATION_SETUP_STEP_META.WELCOME;

const WELCOME_FEATURES = [
  {
    icon: "🥗",
    titleKey: "NUTRITION_INTRO_LBL_FEATURE1_TITLE",
    descKey: "NUTRITION_INTRO_DESC_FEATURE1",
  },
  {
    icon: "💳",
    titleKey: "NUTRITION_INTRO_LBL_FEATURE2_TITLE",
    descKey: "NUTRITION_INTRO_DESC_FEATURE2",
  },
  {
    icon: "📅",
    titleKey: "NUTRITION_INTRO_LBL_FEATURE3_TITLE",
    descKey: "NUTRITION_INTRO_DESC_FEATURE3",
  },
];

const Welcome = ({ history, location }) => {
  const { t } = useTranslation();
  const logoAlt = resolveTranslatedText(
    t("NUTRITION_COMMON_LBL_LOGO_ALT", {
      defaultValue: "Husk Nutrition logo",
    }),
    "NUTRITION_COMMON_LBL_LOGO_ALT",
    "Husk Nutrition logo"
  );
  const appStoreAlt = resolveTranslatedText(
    t("NUTRITION_APPDOWNLOAD_ALT_APP_STORE", {
      defaultValue: "Download on the App Store",
    }),
    "NUTRITION_APPDOWNLOAD_ALT_APP_STORE",
    "Download on the App Store"
  );
  const googlePlayAlt = resolveTranslatedText(
    t("NUTRITION_APPDOWNLOAD_ALT_GOOGLE_PLAY", {
      defaultValue: "Get it on Google Play",
    }),
    "NUTRITION_APPDOWNLOAD_ALT_GOOGLE_PLAY",
    "Get it on Google Play"
  );
  const stepTitleRef = useRegistrationStepFocus();
  const search = location?.search || "";
  const codeFromQuery = new URLSearchParams(search).get("code");
  const shouldBypassWelcome =
    typeof codeFromQuery === "string" && codeFromQuery.trim().length > 0;

  useEffect(() => {
    if (isRegistrationComplete()) {
      clearRegistrationData();
    }

    // Deep links with ?code= skip welcome and go straight to account setup
    if (shouldBypassWelcome) {
      trackRegistrationStarted();
      history.replace(`/register/account${search}`);
      return undefined;
    }

    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history, search, shouldBypassWelcome]);

  const handleGetStarted = () => {
    trackRegistrationStarted();
    history.push(`/register/account${search}`);
  };

  if (shouldBypassWelcome) {
    return null;
  }

  return (
    <RegistrationLayout
      containScroll
      header={
        <RegistrationSetupNavbar
          variant="welcome"
          stepMeta={WELCOME_STEP}
          totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
          showBack={false}
        />
      }
    >
      <WelcomeWrapper>
        <div className="scroll-body">
            <div className="content-card">
              <div className="content-card-scroll">
              <div className="hero-section">
                <div className="hero-icon-wrap">
                  <img
                    src={NutritionLogo}
                    alt={logoAlt}
                    className="hero-icon"
                  />
                </div>
                <div className="section-eyebrow">{t("NUTRITION_WELCOME_LBL_EYEBROW")}</div>
                <h1
                  className="section-title"
                  ref={stepTitleRef}
                  {...REGISTRATION_STEP_TITLE_PROPS}
                >
                  {t("NUTRITION_INTRO_LBL_WELCOME_TO")}
                  <br />
                  <em>{t("CM_LBL_HUSK_NUTRITION")}</em>
                </h1>
                <p className="section-sub">
                  {t("NUTRITION_INTRO_DESC_SUBTITLE")}
                </p>
              </div>

              <div className="feature-list">
                {WELCOME_FEATURES.map((feature) => (
                  <div className="feature-item" key={feature.titleKey}>
                    <div className="feature-icon" aria-hidden>{feature.icon}</div>
                    <div>
                      <div className="feature-title">{t(feature.titleKey)}</div>
                      {feature.descKey ? (
                        <div className="feature-desc">{t(feature.descKey)}</div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className="notice-box notice-box-privacy">
                <div className="notice-icon" aria-hidden>
                  🔒
                </div>
                <div className="notice-text">
                  {t("NUTRITION_INTRO_DESC_HIPAA_NOTICE")}
                </div>
              </div>

              <button
                type="button"
                className="get-started-btn"
                onClick={handleGetStarted}
              >
                {t("GS_BTN_GET_STARTED")}
                <RegistrationContinueArrow />
              </button>

              <div className="footer-section">
                <p className="footer-text">
                  {t("NUTRITION_WELCOME_DESC_ALREADY_HAVE_ACCOUNT")}
                </p>
                <div className="app-icon">
                  <a
                    href={appleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={AppStoreImage} alt={appStoreAlt} />
                  </a>
                  <a
                    href={googleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={GooglePlayImage} alt={googlePlayAlt} />
                  </a>
                </div>
              </div>
              </div>
            </div>
        </div>
      </WelcomeWrapper>
    </RegistrationLayout>
  );
};

export default withRouter(Welcome);
